import React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Pill } from '../../components/ui';
import type { AdaptedReport } from '../../components/titleAnalysis/reportAdapter';
import { displayList } from '../../components/titleAnalysis/displayUtils/displayList';
import { displayValue } from '../../components/titleAnalysis/displayUtils/displayValue';
import { formatMoney, lienPositionLabel, lienStatusMeta } from './reportModel';
import { Field, Overline } from './reportParts';

/**
 * The three report status cards.
 *
 * Requirement 11.7: subject lien position (expected vs derived with status
 * Confirmed / Mismatch / Undetermined), current vested owner (and whether it
 * matches the subject mortgagor), search coverage (start and effective dates,
 * minimum period, bring-down gap).
 */

const CardShell: React.FC<{
  title: string;
  testId: string;
  status: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, testId, status, children }) => (
  <section
    data-testid={testId}
    aria-label={title}
    className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-[18px]"
  >
    <div className="flex items-start justify-between gap-2">
      <Overline>{title}</Overline>
      {status}
    </div>
    {children}
  </section>
);

export const SubjectLienCard: React.FC<{
  lien: AdaptedReport['lienPosition'];
  subjectLoan: AdaptedReport['subjectLoan'];
}> = ({ lien, subjectLoan }) => {
  const status = lienStatusMeta(lien.status);
  const hasLoanFacts =
    subjectLoan.currentLoanHolder != null ||
    subjectLoan.loanAmount != null ||
    subjectLoan.noteDate != null;
  return (
    <CardShell
      title="Subject lien position"
      testId="title-analysis-lien-card"
      status={<Pill tone={status.tone}>{status.label}</Pill>}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Expected">
          <span className="text-base font-bold">{lienPositionLabel(lien.expected)}</span>
        </Field>
        <Field label="Derived">
          <span className="text-base font-bold">{lienPositionLabel(lien.derived)}</span>
        </Field>
      </div>
      <p className="text-[13px] text-gray-700">{displayValue(lien.explanation)}</p>
      <Field label="Senior open encumbrances">{displayList(lien.seniorOpenEncumbranceIds)}</Field>
      {hasLoanFacts ? (
        <div
          data-testid="title-analysis-subject-loan"
          className="border-t border-gray-100 pt-3 text-[13px] text-gray-600"
        >
          Holder {displayValue(subjectLoan.currentLoanHolder)} · Amount{' '}
          {formatMoney(subjectLoan.loanAmount)} · Note date {displayValue(subjectLoan.noteDate)}
        </div>
      ) : null}
    </CardShell>
  );
};

export const VestedOwnerCard: React.FC<{ owner: AdaptedReport['currentVestedOwner'] }> = ({
  owner,
}) => (
  <CardShell
    title="Current vested owner"
    testId="title-analysis-owner-card"
    status={
      owner.matchesSubjectMortgagor ? (
        <Pill tone="success" icon={<CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />}>
          Matches mortgagor
        </Pill>
      ) : (
        <Pill tone="danger" icon={<XCircle className="h-3.5 w-3.5" aria-hidden="true" />}>
          Does not match mortgagor
        </Pill>
      )
    }
  >
    <div className="text-base font-bold text-gray-900">
      {displayList(owner.owners, displayValue(null))}
    </div>
    <Field label="Matches subject mortgagor">{displayValue(owner.matchesSubjectMortgagor)}</Field>
    <p className="text-[13px] text-gray-700">{displayValue(owner.notes)}</p>
  </CardShell>
);

export const SearchCoverageCard: React.FC<{ coverage: AdaptedReport['searchCoverage'] }> = ({
  coverage,
}) => (
  <CardShell
    title="Search coverage"
    testId="title-analysis-coverage-card"
    status={
      coverage.meetsMinimumPeriod ? (
        <Pill tone="success">Meets minimum</Pill>
      ) : (
        <Pill tone="danger">Below minimum</Pill>
      )
    }
  >
    <div className="grid grid-cols-2 gap-3">
      <Field label="Search start">{displayValue(coverage.searchStartDate)}</Field>
      <Field label="Effective date">{displayValue(coverage.searchEffectiveDate)}</Field>
      <Field label="Minimum period">
        {coverage.meetsMinimumPeriod ? 'Met' : 'Not met'}
      </Field>
      <Field label="Bring-down gap">{displayValue(coverage.bringDownGapDays)} days</Field>
    </div>
    {coverage.gaps.length > 0 ? <Field label="Gaps">{displayList(coverage.gaps)}</Field> : null}
  </CardShell>
);

export const StatusCards: React.FC<{ report: AdaptedReport }> = ({ report }) => (
  <div data-testid="title-analysis-status-cards" className="grid grid-cols-1 gap-4 lg:grid-cols-3">
    <SubjectLienCard lien={report.lienPosition} subjectLoan={report.subjectLoan} />
    <VestedOwnerCard owner={report.currentVestedOwner} />
    <SearchCoverageCard coverage={report.searchCoverage} />
  </div>
);
